import { computed, watch } from 'vue';
import { useColorMode } from '@vueuse/core';

/**
 * User-facing theme preference. `system` follows the OS setting; `light` /
 * `dark` pin a specific appearance. Dark is the product default.
 */
export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'teamango-color-mode';

// Background colors used to keep the browser UI (mobile address bar / PWA
// chrome) in sync with the active theme. Mirrors the html background in
// `styles/style.css`.
const THEME_COLORS: Record<'light' | 'dark', string> = {
  light: 'lch(96.3% 0.5 282)',
  dark: 'lch(4.52% 0.3 272)',
};

// Nuxt UI's built-in color-mode plugin is disabled (`colorMode: false` in
// vite.config.ts), so we drive VueUse's `useColorMode` ourselves. Created once
// at module scope so every caller — the profile selector and the app bootstrap
// — shares a single instance and a single set of DOM effects.
//
// `emitAuto` keeps `system` a first-class value (returned as `'auto'`) instead
// of collapsing it to the resolved light/dark. `initialValue: 'dark'` makes the
// first visit dark, matching the hardcoded `class="dark"` in index.html and the
// no-flash inline script that reads the same storage key.
const colorMode = useColorMode({
  storageKey: STORAGE_KEY,
  initialValue: 'dark',
  emitAuto: true,
  // `attribute: 'class'` and the <html> selector are VueUse defaults: it
  // toggles the `dark` / `light` class the stylesheet keys off of.
});

// Keep <meta name="theme-color"> aligned with the resolved appearance.
watch(
  () => colorMode.state.value,
  (resolved) => {
    const meta = document.querySelector('meta[name="theme-color"]');
    meta?.setAttribute('content', THEME_COLORS[resolved === 'dark' ? 'dark' : 'light']);
  },
  { immediate: true },
);

// Bridge the public `system` label to VueUse's `auto` value.
const preference = computed<ThemePreference>({
  get() {
    return colorMode.value === 'auto' ? 'system' : (colorMode.value as ThemePreference);
  },
  set(value) {
    colorMode.value = value === 'system' ? 'auto' : value;
  },
});

/**
 * Theme controls. `preference` is the user's chosen setting
 * (`system` | `light` | `dark`); `isDark` reflects the resolved appearance
 * after `system` is evaluated against the OS.
 */
export const useTheme = () => {
  const isDark = computed(() => colorMode.state.value === 'dark');

  return {
    preference,
    isDark,
  };
};
